import { SdbMemberTypes } from "@custom-types/member-types";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { OpenTeamDetails, useTeamUtils } from "@hooks/use-team-utils";
import { useToast } from "@flat-ui";
import { TeamTabs } from "@router/route-params";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useCompanyId } from "@context-providers/company-id-provider";
import { isTeam } from "@utils/team-utils";
import {
  TeamEvents,
  WorkspaceMemberEvents,
} from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";

interface OpenProfilePage {
  /** The source place from where the profile is opening */
  source: string;

  /** Contains the user id or identity */
  member?: SdbMemberTypes | FaroCoreApiTypes.UserAsManager | null;
}

interface UseMembersUtils {
  /** Callback function to open profile page of a user */
  openProfilePage(params: OpenProfilePage): void;

  /** Callback function to open team page of a user */
  openViewTeamMembers(params: OpenTeamDetails): void;
}

/**
 * @returns utility functions to open and navigate to member related pages
 */
export function useMembersUtils(): UseMembersUtils {
  const companyId = useCompanyId();
  const { navigateToMemberProfile, navigateToTeamMembers } = useAppNavigation();
  const { openTeamDetails } = useTeamUtils();
  const { openToast } = useToast();
  const { trackEvent } = useTrackEvent();

  /**
   * Opens the team details page if the required parameters are available.
   *
   * @param {string} param0.teamId - The unique identifier of the team.
   * @param {number} [param0.numberOfMembers] - The number of members in the team (optional).
   */
  function openViewTeamMembers({
    teamId,
    numberOfMembers,
  }: OpenTeamDetails): void {
    trackEvent({
      name: TeamEvents.openTeam,
      props: {
        teamId,
        tab: TeamTabs.members,
        numberOfMembers: numberOfMembers ?? "",
      },
    });

    navigateToTeamMembers({
      companyId,
      teamId,
    });
  }

  function openProfilePage({ source, member }: OpenProfilePage): void {
    trackEvent({
      name: WorkspaceMemberEvents.openMemberProfilePage,
      props: { source },
    });

    if (!member) {
      openToast({
        title: "User profile page cannot be opened",
        variant: "error",
        message:
          "User ID not available. " + "Please reload the page to try again.",
      });

      return;
    }

    if (isTeam(member)) {
      openTeamDetails({ teamId: member.identity });
      return;
    }

    navigateToMemberProfile({
      companyId,
      // Encode memberId since it can be an email address and have special characters
      // That might conflict with the URL special characters
      memberId: encodeURIComponent(member.identity),
    });
  }

  return {
    openProfilePage,
    openViewTeamMembers,
  };
}
